import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, MenuItem, Select, CircularProgress, Autocomplete } from '@mui/material';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import WifiRoundedIcon from '@mui/icons-material/WifiRounded';
import LightbulbRoundedIcon from '@mui/icons-material/LightbulbRounded';
import TouchAppRoundedIcon from '@mui/icons-material/TouchAppRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import httpcommon from '../../helper/httpcommon';

const NAVY = '#292D5B';
const INK = '#3E4A66';
const ORANGE = '#F6A03A';
const TEAL = '#0E9BA6';

// Placeholder locality options for the address picker. Edit this list to
// match the localities relevant to your schools.
const ADDRESS_OPTIONS = [
  'Salt Lake, Kolkata',
  'Park Street, Kolkata',
  'New Town, Kolkata',
  'Ballygunge, Kolkata',
  'Behala, Kolkata',
  'Garia, Kolkata',
  'Dum Dum, Kolkata',
  'Howrah, Kolkata',
];

const BEFORE = [
  {
    icon: <MenuBookRoundedIcon />,
    lead: 'Keep your mark sheets handy',
    text: ' — they help you reflect on how you’ve done so far.',
  },
  {
    icon: <WifiRoundedIcon />,
    text: 'Find a quiet, distraction-free spot with a stable internet connection.',
  },
];

const DURING = [
  { icon: <LightbulbRoundedIcon />, text: 'You’ll see real-life situations you might actually run into.' },
  { icon: <TouchAppRoundedIcon />, text: 'Read each one carefully and pick the response that feels most like you.' },
  { icon: <TimerRoundedIcon />, text: 'Give each question 2–5 minutes — take your time, but don’t overthink it.' },
  { icon: <FavoriteRoundedIcon />, text: 'Answer honestly. That’s what makes your result actually useful.' },
  {
    icon: <LockRoundedIcon />,
    text: 'Once you submit an answer it locks, so you can’t go back and change it.',
    highlight: true,
  },
];

const Row = ({ item }) => (
  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', py: 1.15 }}>
    <Box
      sx={{
        flexShrink: 0,
        width: 32,
        height: 32,
        borderRadius: '9px',
        backgroundColor: item.highlight ? '#FFF3E1' : '#F1F1F7',
        color: item.highlight ? '#D9820E' : '#6B7186',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& svg': { fontSize: 18 },
      }}
    >
      {item.icon}
    </Box>
    <Typography sx={{ fontSize: 14, color: INK, lineHeight: 1.6, pt: 0.35 }}>
      {item.lead ? <strong style={{ color: NAVY }}>{item.lead}</strong> : null}
      {item.text}
    </Typography>
  </Box>
);

const Section = ({ icon, label, accent, items }) => (
  <Box
    sx={{
      backgroundColor: '#FFFFFF',
      border: '1px solid #E7EAF1',
      borderRadius: '18px',
      overflow: 'hidden',
      mb: 2,
      boxShadow: '0 6px 20px rgba(41, 45, 91, 0.05)',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: { xs: 2, sm: 2.5 },
        py: 1.5,
        borderBottom: '1px solid #EEF0F5',
        backgroundColor: `${accent}0A`,
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: '9px',
          backgroundColor: accent,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& svg': { fontSize: 17 },
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontSize: 12.5,
          fontWeight: 800,
          color: NAVY,
          letterSpacing: '0.6px',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
    </Box>

    <Box
      sx={{
        px: { xs: 2, sm: 2.5 },
        py: 0.75,
        '& > *:not(:last-child)': { borderBottom: '1px dashed #EEF0F5' },
      }}
    >
      {items.map((item, i) => (
        <Row key={i} item={item} />
      ))}
    </Box>
  </Box>
);

const AssessmentGuidelines = ({ onStart }) => {
  const [profile, setProfile] = useState({ school_name: '', board: '', student_class: '', address: '' });
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [errors, setErrors] = useState({});
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [addingNewSchool, setAddingNewSchool] = useState(false);
  const [addingNewAddress, setAddingNewAddress] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      httpcommon.get('school/student/profile/'),
      httpcommon.get('school/student/school-options/'),
    ])
      .then(([profileResult, schoolsResult]) => {
        if (cancelled) return;
        const data = profileResult.status === 'fulfilled' ? profileResult.value?.data || {} : {};
        const rawSchools = schoolsResult.status === 'fulfilled' ? schoolsResult.value?.data?.schools : [];
        const schools = Array.isArray(rawSchools) ? rawSchools
          .map((school) => ({ name: String(school?.name || '').trim(), board: String(school?.board || '').trim() }))
          .filter((school) => school.name)
          .sort((a, b) => a.name.localeCompare(b.name)) : [];
        setSchoolOptions(schools);
        const savedSchool = data.school || data.school_name || '';
        const matchingSchool = schools.find((school) => school.name.toLowerCase() === String(savedSchool).toLowerCase());
        const savedAddress = String(data.address || '').trim();
        const matchingAddress = ADDRESS_OPTIONS.find((option) => option.toLowerCase() === savedAddress.toLowerCase());
        setProfile({
          school_name: matchingSchool?.name || '',
          board: matchingSchool?.board || data.board || '',
          student_class: String(data.student_class || ''),
          address: matchingAddress || savedAddress,
        });
        // New-school mode is an explicit student choice from the selector,
        // never an automatic fallback for an older profile value.
        setAddingNewSchool(false);
        // A saved address that isn't in the preset list falls back to the
        // custom-entry field so it doesn't look lost/blank.
        setAddingNewAddress(Boolean(savedAddress) && !matchingAddress);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingProfile(false); });
    return () => { cancelled = true; };
  }, []);

  const handleStart = () => {
    // Every attempt begins with this review, including returning students.
    // Their saved details are already prefilled, so confirmation is quick.
    setErrors({});
    setProfileDialogOpen(true);
  };

  const updateProfile = (field, value) => {
    setProfile((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: false }));
  };

  const selectSchool = (_, value) => {
    if (value === '__add_new_school__') {
      setAddingNewSchool(true);
      setProfile((current) => ({ ...current, school_name: '', board: '' }));
      setErrors((current) => ({ ...current, school_name: false, board: false }));
      return;
    }
    const schoolName = typeof value === 'string' ? value : value?.name || '';
    const selectedSchool = schoolOptions.find((school) => school.name === schoolName);
    setAddingNewSchool(false);
    setProfile((current) => ({ ...current, school_name: schoolName, board: selectedSchool?.board || current.board }));
    setErrors((current) => ({ ...current, school_name: false, board: false }));
  };

  const selectAddress = (_, value) => {
    if (value === '__add_new_address__') {
      setAddingNewAddress(true);
      setProfile((current) => ({ ...current, address: '' }));
      return;
    }
    setAddingNewAddress(false);
    setProfile((current) => ({ ...current, address: value || '' }));
  };

  const selectedSchoolHasFixedBoard = Boolean(
    !addingNewSchool &&
    schoolOptions.some((school) => school.name === profile.school_name)
  );

  const saveProfileAndStart = async () => {
    const nextErrors = {
      school_name: !profile.school_name.trim(),
      board: !profile.board.trim(),
      student_class: !profile.student_class.trim(),
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;
    try {
      setSavingProfile(true);
      await httpcommon.post('school/student/profile/', {
        school_name: profile.school_name.trim(),
        board: profile.board,
        board_name: profile.board === 'Other' ? 'Other' : '',
        student_class: profile.student_class,
        address: (profile.address || '').trim(),
      });
      setProfileDialogOpen(false);
      onStart();
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 620, width: '100%', mt: 1, pb: { xs: 6, sm: 2 } }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 800,
          color: ORANGE,
          letterSpacing: '2px',
          textTransform: 'uppercase',
          mb: 0.75,
        }}
      >
        Before you start
      </Typography>
      <Typography sx={{ fontSize: 20, fontWeight: 800, color: NAVY, mb: 0.75, lineHeight: 1.25 }}>
        A quick briefing
      </Typography>
      <Typography sx={{ fontSize: 13.5, color: '#697089', mb: 3, lineHeight: 1.6 }}>
        Takes a minute to read — it’s what keeps your results honest and genuinely about you.
      </Typography>

      <Section icon={<ChecklistRoundedIcon />} label="Get set up" accent={TEAL} items={BEFORE} />
      <Section
        icon={<PlayCircleFilledRoundedIcon />}
        label="While you take it"
        accent={ORANGE}
        items={DURING}
      />

      <Button
        variant="contained"
        onClick={handleStart}
        disabled={loadingProfile}
        endIcon={<EastRoundedIcon />}
        sx={{
          mt: 1.5,
          width: { xs: '100%', sm: 'auto' },
          backgroundColor: ORANGE,
          borderRadius: '12px',
          px: 4.5,
          py: 1.25,
          fontSize: 15,
          fontWeight: 700,
          textTransform: 'none',
          boxShadow: '0 10px 24px rgba(246, 160, 58, 0.32)',
          transition: 'transform .18s ease, box-shadow .18s ease, background-color .18s ease',
          '&:hover': {
            backgroundColor: '#E8912B',
            boxShadow: '0 12px 28px rgba(246, 160, 58, 0.42)',
            transform: 'translateY(-1px)',
          },
        }}
      >
        I’m ready — let’s start
      </Button>
      <Dialog open={profileDialogOpen} onClose={() => !savingProfile && setProfileDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}>
        <DialogTitle sx={{ px: 3, py: 2.5, background: 'linear-gradient(135deg, #292D5B, #46428B)', color: '#fff', position: 'relative', overflow: 'hidden' }}>
          <Box sx={{ position: 'absolute', width: 150, height: 150, right: -62, top: -75, borderRadius: '50%', background: 'radial-gradient(circle, rgba(246,160,58,.48), transparent 68%)' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: '12px', display: 'grid', placeItems: 'center', backgroundColor: 'rgba(255,255,255,0.14)' }}><SchoolRoundedIcon /></Box>
            <Box><Typography sx={{ fontSize: 18, fontWeight: 800 }}>A quick setup</Typography><Typography sx={{ fontSize: 12.5, opacity: 0.76, mt: 0.2 }}>Three details before your assessment</Typography></Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: '24px !important', pb: 1 }}>
          <Typography sx={{ fontSize: 13.5, color: INK, lineHeight: 1.6, mb: 2.5 }}>Please review these details before you begin. They help us tailor questions and recommendations to your school level.</Typography>
          {addingNewSchool ? (
            <Box sx={{ mb: 1.5 }}>
              <TextField fullWidth autoFocus label="School name" value={profile.school_name} onChange={(event) => updateProfile('school_name', event.target.value)} error={Boolean(errors.school_name)} helperText={errors.school_name ? 'Please enter your school name.' : 'This school will be added to the list.'} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
              <Button onClick={() => { setAddingNewSchool(false); updateProfile('school_name', ''); }} sx={{ mt: 0.35, px: 0.5, textTransform: 'none', color: '#565D78', fontWeight: 700, fontSize: 12.5 }}>Choose from school list</Button>
            </Box>
          ) : (
            <Autocomplete
              fullWidth
              options={['__add_new_school__', ...schoolOptions.map((school) => school.name)]}
              value={profile.school_name || null}
              onChange={selectSchool}
              getOptionLabel={(option) => option === '__add_new_school__' ? 'Add a new school' : option}
              filterOptions={(options, params) => {
                const search = params.inputValue.trim().toLowerCase();
                return ['__add_new_school__', ...options.filter((option) => option !== '__add_new_school__' && option.toLowerCase().includes(search))];
              }}
              renderOption={(props, option) => option === '__add_new_school__' ? (
                <Box component="li" {...props} sx={{ ...props.sx, mx: 0.75, mb: 0.75, mt: 0.5, py: 1, borderBottom: '1px dashed #D6CBEA', color: NAVY, fontWeight: 800, backgroundColor: '#F8F4FF', borderRadius: '9px', '&:hover': { backgroundColor: '#F0E8FF' } }}>+ Add a new school</Box>
              ) : <Box component="li" {...props}>{option}</Box>}
              renderInput={(params) => <TextField {...params} label="School name" placeholder="Choose your school" error={Boolean(errors.school_name)} helperText={errors.school_name ? 'Please select or add your school.' : ' '} sx={{ mb: 0.5, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />}
            />
          )}
          <FormControl fullWidth error={Boolean(errors.board)} sx={{ mb: 2 }}>
            <InputLabel>Board</InputLabel>
            <Select label="Board" value={profile.board} disabled={selectedSchoolHasFixedBoard} onChange={(event) => updateProfile('board', event.target.value)} sx={{ borderRadius: '12px' }}>
              <MenuItem value="CBSE">CBSE</MenuItem><MenuItem value="ICSE">ICSE</MenuItem><MenuItem value="State Board">State Board</MenuItem><MenuItem value="IB">IB</MenuItem><MenuItem value="Other">Other</MenuItem>
            </Select>
            {errors.board && <Typography sx={{ color: '#D32F2F', fontSize: 12, mt: 0.5, ml: 1.75 }}>Please select your board.</Typography>}
            {selectedSchoolHasFixedBoard && <Typography sx={{ color: '#697089', fontSize: 11.5, mt: 0.65, ml: 1.75 }}>Board is set by the school you selected.</Typography>}
          </FormControl>
          <FormControl fullWidth error={Boolean(errors.student_class)}>
            <InputLabel>Class</InputLabel>
            <Select label="Class" value={profile.student_class} onChange={(event) => updateProfile('student_class', event.target.value)} sx={{ borderRadius: '12px' }}>
              {['8', '9', '10', '11', '12'].map((grade) => <MenuItem key={grade} value={grade}>Class {grade}</MenuItem>)}
            </Select>
            {errors.student_class && <Typography sx={{ color: '#D32F2F', fontSize: 12, mt: 0.5, ml: 1.75 }}>Please select your class.</Typography>}
          </FormControl>

          <Box sx={{ mt: 2 }}>
            {addingNewAddress ? (
              <Box>
                <TextField fullWidth autoFocus label="Address" placeholder="E.g. Salt Lake, Kolkata" value={profile.address} onChange={(event) => updateProfile('address', event.target.value)} helperText="This address will be added to the list." sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                <Button onClick={() => { setAddingNewAddress(false); updateProfile('address', ''); }} sx={{ mt: 0.35, px: 0.5, textTransform: 'none', color: '#565D78', fontWeight: 700, fontSize: 12.5 }}>Choose from address list</Button>
              </Box>
            ) : (
              <Autocomplete
                fullWidth
                options={['__add_new_address__', ...ADDRESS_OPTIONS]}
                value={profile.address || null}
                onChange={selectAddress}
                getOptionLabel={(option) => option === '__add_new_address__' ? 'Add a new address' : option}
                filterOptions={(options, params) => {
                  const search = params.inputValue.trim().toLowerCase();
                  return ['__add_new_address__', ...options.filter((option) => option !== '__add_new_address__' && option.toLowerCase().includes(search))];
                }}
                renderOption={(props, option) => option === '__add_new_address__' ? (
                  <Box component="li" {...props} sx={{ ...props.sx, mx: 0.75, mb: 0.75, mt: 0.5, py: 1, borderBottom: '1px dashed #D6CBEA', color: NAVY, fontWeight: 800, backgroundColor: '#F8F4FF', borderRadius: '9px', '&:hover': { backgroundColor: '#F0E8FF' } }}>+ Add a new address</Box>
                ) : <Box component="li" {...props}>{option}</Box>}
                renderInput={(params) => <TextField {...params} label="Address" placeholder="Choose your area" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
          <Button onClick={() => setProfileDialogOpen(false)} disabled={savingProfile} sx={{ textTransform: 'none', color: '#6B7186', fontWeight: 700 }}>Not now</Button>
          <Button variant="contained" onClick={saveProfileAndStart} disabled={savingProfile} sx={{ textTransform: 'none', borderRadius: '11px', px: 2.5, py: 1, fontWeight: 800, backgroundColor: ORANGE, '&:hover': { backgroundColor: '#E8912B' } }}>{savingProfile ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save & start'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssessmentGuidelines;
